<template>
  <div class="form-group"
       :class="{
          'input-group': hasIcon,
          'input-group-focus': focused
       }">
    <slot name="label">
      <label v-if="label" :for="inputId" class="control-label">
        {{ label }}<span v-if="required" aria-hidden="true"> *</span>
      </label>
    </slot>
    <slot name="addonLeft">
      <span v-if="addonLeftIcon" class="input-group-prepend">
        <div class="input-group-text">
          <i :class="addonLeftIcon"></i>
        </div>
      </span>
    </slot>
    <slot>
      <input
        :id="inputId"
        :value="modelValue"
        v-bind="$attrs"
        :disabled="disabled"
        :required="required"
        :aria-invalid="error ? 'true' : undefined"
        :aria-describedby="describedBy || undefined"
        @input="updateInput"
        @focus="onFocus"
        @blur="onBlur"
        class="form-control"
        :class="{ 'is-invalid': !!error }">
    </slot>
    <slot name="addonRight">
      <span v-if="addonRightIcon" class="input-group-append">
        <div class="input-group-text">
          <i :class="addonRightIcon"></i>
        </div>
      </span>
    </slot>
    <p v-if="error" :id="errorId" class="form-error" role="alert">{{ error }}</p>
    <p v-else-if="hint" :id="hintId" class="form-hint">{{ hint }}</p>
    <slot name="helperText"></slot>
  </div>
</template>

<script>
  import { defineComponent } from 'vue';

  export default defineComponent({
    inheritAttrs: false,
    name: "BaseInput",
    props: {
      label: {
        type: String,
        description: "Input label"
      },
      modelValue: {
        type: [String, Number],
        description: "Input value"
      },
      addonRightIcon: {
        type: String,
        description: "Input icon on the right"
      },
      addonLeftIcon: {
        type: String,
        description: "Input icon on the left"
      },
      error: {
        type: String,
        default: ""
      },
      hint: {
        type: String,
        default: ""
      },
      disabled: {
        type: Boolean,
        default: false
      },
      required: {
        type: Boolean,
        default: false
      },
      id: {
        type: String,
        default: ""
      },
    },
    data() {
      return {
        focused: false,
        autoId: `bi-${Math.random().toString(36).slice(2, 8)}`
      }
    },
    computed: {
      hasIcon() {
        const { addonRight, addonLeft } = this.$slots;
        return addonRight !== undefined || addonLeft !== undefined || this.addonRightIcon !== undefined || this.addonLeftIcon !== undefined;
      },
      inputId() {
        return this.id || this.autoId;
      },
      errorId() {
        return `${this.inputId}-error`;
      },
      hintId() {
        return `${this.inputId}-hint`;
      },
      describedBy() {
        if (this.error) return this.errorId;
        if (this.hint) return this.hintId;
        return "";
      }
    },
    methods: {
      updateInput(event) {
        this.$emit("update:modelValue", event.target.value);
      },
      onFocus() {
        this.focused = true;
      },
      onBlur() {
        this.focused = false;
      }
    }
  });
</script>

<style scoped>
.form-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-bottom: 18px;
}
.form-group.input-group {
  flex-direction: row;
  align-items: stretch;
  gap: 0;
}
.control-label {
  font-size: 0.76rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--adm-muted);
  margin: 0;
}
.form-control {
  width: 100%;
  background: var(--adm-card);
  border: 1px solid var(--adm-border-strong);
  border-radius: var(--adm-radius-sm);
  color: var(--adm-text);
  padding: 10px 13px;
  font-size: 0.9rem;
  line-height: 1.4;
  height: auto;
  box-shadow: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}
.form-control::placeholder { color: var(--adm-muted); opacity: 0.65; }
.form-control:focus {
  outline: none;
  border-color: var(--adm-accent);
  box-shadow: 0 0 0 3px var(--adm-accent-soft);
  background: var(--adm-card);
}
.input-group .form-control {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
.input-group-text {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--adm-card-head);
  border: 1px solid var(--adm-border-strong);
  border-right: none;
  border-radius: var(--adm-radius-sm) 0 0 var(--adm-radius-sm);
  color: var(--adm-muted);
  padding: 0 12px;
  transition: color 0.15s ease, border-color 0.15s ease;
}
.input-group-text i { font-size: 0.9rem; }
.input-group-focus .input-group-text {
  color: var(--adm-accent);
  border-color: var(--adm-accent);
}
input.form-control[type="date"],
input.form-control[type="number"] { color-scheme: light; }
.dark-mode input.form-control[type="date"],
.dark-mode input.form-control[type="number"] { color-scheme: dark; }
.form-control.is-invalid { border-color: var(--adm-danger); }
.form-control.is-invalid:focus { border-color: var(--adm-danger); box-shadow: 0 0 0 3px var(--adm-danger-soft); }
.form-error { margin: 0; font-size: 0.78rem; color: var(--adm-danger); }
.form-hint { margin: 0; font-size: 0.78rem; color: var(--adm-muted); }
</style>